/**
 * Move Player & Helmet Tool - Main Component
 * 
 * This component provides the ability to move a player and their assigned
 * helmets from one organization/team to another using a progressive disclosure
 * workflow consistent with existing features.
 * 
 * Features:
 * - Progressive disclosure UI matching existing tools
 * - Player selection with roster integration
 * - Equipment transfer management
 * - Comprehensive validation and error handling
 * - Duplicate name detection per requirements
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import Button from '../shared/Button'
import ResetButton from '../shared/ResetButton'
import SearchableDropdown from '../shared/SearchableDropdown'
import API from '../../API/API'
import { useAlerts } from '../../contexts/AlertContext'
import logger from '../../utils/logger'

// Import shared types
import { Organization, Team } from '../../types/shared'
import { 
  MovePlayerHelmetFormData, 
  MovePlayerHelmetState, 
  RosterPlayer
} from '../../types/movePlayerHelmet'

// Import shared utilities
import { 
  validatePlayerTransfer, 
  formatPlayerName,
  handleOrganizationSelection,
  createOrgTeamDisplayName
} from '../../utils/shared/validation'
import { formatFeatures, isConvertibleHelmet, isSFEchoHelmet } from '../../utils/helmetLookup'

// Convertible / SF Echo helmets can only land on a generic "RIDDELL {hat size}" player in the target team
interface RestrictedHelmetCheck {
  playerUnitID: number
  label: string
  features: string
  hatSize: string
  requiredPlayerName: string
  isValid: boolean
}

function MovePlayerHelmetTool() {
  // Form management
  const { register, handleSubmit, formState: { errors }, watch, setValue, reset } = useForm<MovePlayerHelmetFormData>({
    defaultValues: {
      sourceOrgID: '',
      sourceTeamID: '',
      playerID: '',
      targetOrgID: '',
      targetTeamID: '',
      equipmentTransferMode: 'with-equipment',  // Default to transferring with equipment
      confirmTransfer: false
    }
  })

  // Component state
  const [state, setState] = useState<MovePlayerHelmetState>({
    // Selection states
    sourceOrg: null,
    sourceTeam: null,
    selectedPlayer: null,
    targetOrg: null,
    targetTeam: null,
    
    // Data states
    organizations: [],
    sourceTeams: [],
    targetTeams: [],
    rosterPlayers: [],
    
    // Loading states
    isLoadingOrgs: false,
    isLoadingTeams: false,  // For source teams
    isLoadingPlayers: false,
    isSubmitting: false,
    
    // Simple error states matching other features
    orgLoadError: null,
    teamLoadError: null,
    sourceTeamLoadError: null,
    targetTeamLoadError: null,
    playerLoadError: null,
    submitError: null,
    
    // UI states
    currentStep: 'source',
    showConfirmation: false,
    validationErrors: {}
  })

  const [restrictedHelmetChecks, setRestrictedHelmetChecks] = useState<RestrictedHelmetCheck[] | null>(null)
  const [isValidatingRestrictedHelmets, setIsValidatingRestrictedHelmets] = useState(false)
  const [restrictedHelmetError, setRestrictedHelmetError] = useState<string | null>(null)

  const { addAlert } = useAlerts()

  // Watch form values for progressive disclosure
  const watchedSourceOrgID = watch('sourceOrgID')
  const watchedSourceTeamID = watch('sourceTeamID')
  const watchedPlayerID = watch('playerID')
  const watchedTargetOrgID = watch('targetOrgID')
  const watchedTargetTeamID = watch('targetTeamID')
  const watchedEquipmentMode = watch('equipmentTransferMode')

  // Helmets that require a matching "RIDDELL {hat size}" player on the target team
  const restrictedEquipment = useMemo(() => {
    if (watchedEquipmentMode === 'without-equipment') return []
    return (state.selectedPlayer?.equipment || []).filter(eq => isConvertibleHelmet(eq) || isSFEchoHelmet(eq))
  }, [state.selectedPlayer, watchedEquipmentMode])

  const invalidRestrictedHelmets = useMemo(
    () => (restrictedHelmetChecks || []).filter(check => !check.isValid),
    [restrictedHelmetChecks]
  )

  const isRestrictedTransferBlocked = restrictedEquipment.length > 0 &&
    (isValidatingRestrictedHelmets || !!restrictedHelmetError || invalidRestrictedHelmets.length > 0 || restrictedHelmetChecks === null)

  // Validate convertible / SF Echo helmets against the target team roster
  useEffect(() => {
    const targetTeamID = state.targetTeam?.teamID

    if (!targetTeamID || restrictedEquipment.length === 0) {
      setRestrictedHelmetChecks(null)
      setRestrictedHelmetError(null)
      return
    }

    let cancelled = false

    const validateRestrictedHelmets = async () => {
      setIsValidatingRestrictedHelmets(true)
      setRestrictedHelmetError(null)

      try {
        const response = await API.GetRoster(targetTeamID, true)
        const players = response.data || []

        const checks: RestrictedHelmetCheck[] = restrictedEquipment.map(eq => {
          const hatSize = (eq.helmetHatSize || '').trim()
          const hasMatchingPlayer = !!hatSize && players.some((player: any) =>
            player.firstName?.toUpperCase() === 'RIDDELL' &&
            player.lastName?.toUpperCase() === hatSize.toUpperCase()
          )

          return {
            playerUnitID: eq.playerUnitID,
            label: eq.helmetRFIDLabel || eq.playerUnitSerialString || `Unit ${eq.playerUnitID}`,
            features: formatFeatures(eq.features),
            hatSize,
            requiredPlayerName: `RIDDELL ${hatSize || 'N/A'}`,
            isValid: hasMatchingPlayer
          }
        })

        if (!cancelled) {
          setRestrictedHelmetChecks(checks)
        }
      } catch (error: any) {
        logger.error('Failed to validate restricted helmets against target roster', {
          module: 'MovePlayerHelmetTool',
          data: { error: error.message, targetTeamID }
        })
        if (!cancelled) {
          setRestrictedHelmetChecks(null)
          setRestrictedHelmetError('Failed to validate Convertible / SF Echo helmet requirements. Please try again.')
        }
      } finally {
        if (!cancelled) {
          setIsValidatingRestrictedHelmets(false)
        }
      }
    }

    validateRestrictedHelmets()

    return () => { cancelled = true }
  }, [state.targetTeam?.teamID, restrictedEquipment])

  // Simple error handling matching other features
  const handleApiError = useCallback((error: any, defaultMessage: string) => {
    const errorMessage = error.response?.data?.message || error.message || defaultMessage
    addAlert('error', errorMessage)
  }, [addAlert])

  // Load organizations on component mount
  useEffect(() => {
    loadOrganizations()
  }, [])

  // Handle source organization change
  useEffect(() => {
    if (watchedSourceOrgID) {
      setValue('sourceTeamID', '')
      setValue('playerID', '')
      setValue('targetOrgID', '')
      setValue('targetTeamID', '')
      setValue('confirmTransfer', false)
      setState(prev => ({
        ...prev,
        sourceTeam: null,
        selectedPlayer: null,
        rosterPlayers: [],
        targetOrg: null,
        targetTeam: null,
        targetTeams: [],
        // clear any target-specific team error when changing source org
        targetTeamLoadError: null,
        currentStep: 'source',
      }))
    }
  }, [watchedSourceOrgID, setValue])

  // Ensure team dropdown is reset and visible after org change
  useEffect(() => {
    if (!watchedSourceOrgID) {
      setValue('sourceTeamID', '')
    }
  }, [watchedSourceOrgID, setValue])

  // Handle source team change
  useEffect(() => {
    if (watchedSourceTeamID) {
      const teamId = parseInt(watchedSourceTeamID)
      const team = state.sourceTeams.find(t => t.teamID === teamId)
      if (team && team.teamID !== state.sourceTeam?.teamID) {
        setState(prev => ({
          ...prev,
          sourceTeam: team,
          rosterPlayers: [],
          selectedPlayer: null,
          targetOrg: null,
          targetTeam: null,
          targetTeams: [],
        }))
        setValue('playerID', '')
        setValue('targetOrgID', '')
        setValue('targetTeamID', '')
        setValue('confirmTransfer', false)
        loadRosterPlayers(teamId)
      }
    }
  }, [watchedSourceTeamID, state.sourceTeams, state.sourceTeam?.teamID, setValue])

  // Handle target organization change with automatic team selection
  // When clearing the organization, also hide Step 4 (confirmation) by resetting currentStep
  useEffect(() => {
    if (watchedTargetOrgID) {
      setValue('targetTeamID', '')
      setState(prev => ({
        ...prev,
        targetTeam: null,
        targetTeamLoadError: null
      }))
      const org = state.organizations.find(o => o.orgID === parseInt(watchedTargetOrgID))
      if (org) {
        handleOrganizationSelection(org)
          .then(result => {
            setState(prev => ({
              ...prev,
              targetOrg: org,
              targetTeams: result.teams,
              targetTeamLoadError: null
            }))
            if (!result.requiresTeamSelection && result.team) {
              setState(prev => ({ ...prev, targetTeam: result.team, currentStep: 'confirm' }))
              setValue('targetTeamID', result.team.teamID.toString())
            } else {
              setState(prev => ({ ...prev, targetTeam: null }))
              setValue('targetTeamID', '')
            }
          })
          .catch(error => {
            const msg = error instanceof Error ? error.message : String(error)
            const isNoTeams = typeof msg === 'string' && msg.toLowerCase().includes('no teams found for organization')
            if (isNoTeams) {
              // Show inline message instead of a toast to avoid spamming
              setState(prev => ({ ...prev, targetOrg: null, targetTeams: [], targetTeamLoadError: `No teams found for organization ${org.orgName}` }))
              logger.info('No teams found for target organization', { module: 'MovePlayerHelmetTool', data: { orgID: org.orgID } })
            } else {
              handleApiError(error, 'Failed to load teams for target organization')
              setState(prev => ({ ...prev, targetOrg: null, targetTeams: [], targetTeamLoadError: null }))
            }
          })
      }
    } else {
      // When target organization is cleared, hide Step 4 and reset all target selections
      setState(prev => ({
        ...prev,
        targetOrg: null,
        targetTeam: null,
        targetTeams: [],
        targetTeamLoadError: null,
        currentStep: 'target' // Revert to target selection step, hiding confirmation
      }))
      setValue('targetTeamID', '')
      setValue('confirmTransfer', false)
    }
  }, [watchedTargetOrgID, state.organizations, setValue, handleApiError])

  // Handle player selection
  // When the player changes, clear target organization selections so they must be re-selected
  useEffect(() => {
    if (watchedPlayerID) {
      const player = state.rosterPlayers.find(p => p.playerID === parseInt(watchedPlayerID))
      if (player) {
        setState(prev => ({ ...prev, selectedPlayer: player, currentStep: 'target' }))
        // Clear target organization when player changes (requires re-selection)
        setValue('targetOrgID', '')
        setValue('targetTeamID', '')
        setValue('confirmTransfer', false)
        // Set transfer mode based on whether player has equipment
        const hasEquipment = player.equipment && player.equipment.length > 0
        setValue('equipmentTransferMode', hasEquipment ? 'with-equipment' : 'without-equipment')
      }
    } else {
      // Clear selected player and target organization when playerID is empty
      setState(prev => ({ ...prev, selectedPlayer: null }))
      setValue('targetOrgID', '')
      setValue('targetTeamID', '')
      setValue('confirmTransfer', false)
    }
  }, [watchedPlayerID, state.rosterPlayers, setValue])



  // Handle target team change
  // When clearing the team, also hide Step 4 by resetting currentStep
  useEffect(() => {
    if (watchedTargetTeamID) {
      const team = state.targetTeams.find(t => t.teamID === parseInt(watchedTargetTeamID))
      if (team) {
        setState(prev => ({ ...prev, targetTeam: team, currentStep: 'confirm', targetTeamLoadError: null }))
      }
    } else {
      // When target team is cleared, hide Step 4
      setState(prev => ({
        ...prev,
        targetTeam: null,
        currentStep: 'target' // Revert to target selection step, hiding confirmation
      }))
      setValue('confirmTransfer', false)
    }
  }, [watchedTargetTeamID, state.targetTeams, state.selectedPlayer, state.sourceTeam, setValue])

  /**
   * Load organizations from API
   */
  const loadOrganizations = useCallback(async () => {
    setState(prev => ({ 
      ...prev, 
      isLoadingOrgs: true,
      orgLoadError: null
    }))
    
    try {
      const response = await API.GetOrgs()
      
      if (response.success && response.data) {
        setState(prev => ({ 
          ...prev, 
          organizations: response.data as Organization[],
          isLoadingOrgs: false,
          orgLoadError: null
        }))
      } else {
        throw new Error(response.message || 'Failed to load organizations')
      }
    } catch (error: any) {
      setState(prev => ({ 
        ...prev, 
        isLoadingOrgs: false,
        orgLoadError: 'Error loading organizations'
      }))
      
      // Simple error handling matching other features
      const errorMessage = error.response?.data?.message || error.message || 'Failed to load organizations'
      addAlert('error', errorMessage)
      logger.error('Failed to load organizations', { module: 'MovePlayerHelmetTool', data: { error: error.message } })
    }
  }, [addAlert])

  /**
   * Load players for selected team roster - simplified to match existing features
   */
  const loadRosterPlayers = useCallback(async (teamId: number) => {
    setState(prev => ({ 
      ...prev, 
      isLoadingPlayers: true,
      playerLoadError: null
    }))
    
    try {
      const response = await API.GetRoster(teamId)
      
      if (response.success && response.data) {
        // Group players by playerID and aggregate equipment
        // A player can appear multiple times in the roster data with different equipment assignments
        const playerMap = new Map<number, any>()
        
        response.data
          .filter((player: any) => player.firstName !== 'RIDDELL') // Filter out RIDDELL entries from player list
          .forEach((player: any) => {
            const playerId = player.PT_playerID
            
            // Build equipment object from this roster entry
            const equipmentAssignment: any = {
              playerUnitID: player.playerUnitID || 0,
              playerUnitSerialString: player.playerUnitSerialString || '',
              playersEquipmentID: player.playersEquipmentID || '',
              equipmentType: 'helmet',
              helmetRFIDLabel: player.helmetRFIDLabel || '',
              rfidBarcode: player.rfidBarcode || '',
              helmetHatSize: player.helmetHatSize || null,
              features: player.features || '',
              helmetType: player.helmetType || null,
              helmetColor: player.helmetColor || null,
              playerUnitHardwareNumber: player.playerUnitHardwareNumber || 0,
              playerUnitFirmwareVersion: player.playerUnitFirmwareVersion || '',
              latestFirmwareVersion: player.latestFirmwareVersion || '',
              firmwareUpToDate: player.firmwareUpToDate || false
            }
            
            if (playerMap.has(playerId)) {
              // Merge equipment for duplicate players (same player with different equipment)
              const existingEntry = playerMap.get(playerId)
              const existingEquipment = existingEntry.equipment || []
              
              // Avoid adding duplicate equipment (check by playerUnitID)
              const isDuplicate = existingEquipment.some((eq: any) => eq.playerUnitID === equipmentAssignment.playerUnitID)
              
              if (!isDuplicate && equipmentAssignment.playerUnitID > 0) {
                existingEquipment.push(equipmentAssignment)
              }
              
              playerMap.set(playerId, {
                ...existingEntry,
                equipment: existingEquipment,
                // Ensure RosterPlayer-specific properties are maintained/added
                formattedName: existingEntry.formattedName || formatPlayerName(existingEntry),
                isEligibleForTransfer: existingEntry.isEligibleForTransfer ?? true
              })
            } else {
              // New player - create entry with first equipment
              const equipment = equipmentAssignment.playerUnitID > 0 ? [equipmentAssignment] : []
              playerMap.set(playerId, {
                ...player,
                equipment,
                // Add RosterPlayer-specific properties
                formattedName: formatPlayerName(player),
                isEligibleForTransfer: true
              })
            }
          })
        
        // Convert map values to array and set as roster players
        const playersArray = Array.from(playerMap.values())
        setState(prev => ({ 
          ...prev, 
          rosterPlayers: playersArray,
          // Only advance to the player selection step when we actually have players
          currentStep: playersArray.length > 0 ? 'player' : 'source',
          isLoadingPlayers: false,
          playerLoadError: playersArray.length > 0 ? null : 'No roster players found for this team'
        }))
      } else {
        throw new Error(response.message || 'Failed to load roster players')
      }
    } catch (error: any) {
      // If the API explicitly reports no roster for the team, show an inline
      // message instead of repeatedly firing a toast alert. This avoids the
      // nonstop error message the user reported.
      const errorMessage = error.response?.data?.message || error.message || 'Failed to load roster players'
      const isNoRoster = typeof errorMessage === 'string' && errorMessage.toLowerCase().includes('no roster')

      setState(prev => ({ 
        ...prev, 
        isLoadingPlayers: false,
        playerLoadError: isNoRoster ? `No roster found for team ${teamId}` : 'Error loading roster players',
        // Ensure we don't leave a selected player or advance the workflow
        rosterPlayers: [],
        selectedPlayer: null,
        currentStep: isNoRoster ? 'source' : prev.currentStep
      }))

      // Only show a toast alert for unexpected/unrelated errors
      if (!isNoRoster) {
        addAlert('error', errorMessage)
        logger.error('Failed to load roster players', { module: 'MovePlayerHelmetTool', data: { error: error.message } })
      } else {
        logger.info('No roster found for team', { module: 'MovePlayerHelmetTool', data: { teamId } })
      }
    }
  }, [addAlert])

  /**
   * Load organizations for target selection - simplified to match existing features
   */
  const loadTargetOrganizations = useCallback(async () => {
    if (!state.selectedPlayer || !state.sourceTeam) return
    
    setState(prev => ({ 
      ...prev, 
      isLoadingOrgs: true,
      orgLoadError: null
    }))
    
    try {
      const response = await API.GetOrgs()
      
      if (response.success && response.data) {
        // Filter out source organization
        const filteredOrgs = response.data.filter((org: Organization) =>
          state.sourceTeam ? org.orgID !== state.sourceTeam.orgID : true
        )
        setState(prev => ({ 
          ...prev, 
          organizations: filteredOrgs,
          isLoadingOrgs: false,
          orgLoadError: null
        }))
      } else {
        throw new Error(response.message || 'Failed to load organizations')
      }
    } catch (error: any) {
      setState(prev => ({ 
        ...prev, 
        isLoadingOrgs: false,
        orgLoadError: 'Error loading organizations'
      }))
      
      // Simple error handling matching other features
      const errorMessage = error.response?.data?.message || error.message || 'Failed to load organizations'
      addAlert('error', errorMessage)
      logger.error('Failed to load organizations', { module: 'MovePlayerHelmetTool', data: { error: error.message } })
    }
  }, [addAlert, state.selectedPlayer, state.sourceTeam])

  const handleReset = useCallback(() => {
    // Reset form data
    reset()
    
    // Clear all state variables
    setState(prev => ({
      ...prev,
      sourceOrg: null,
      sourceTeam: null,
      selectedPlayer: null,
      targetOrg: null,
      targetTeam: null,
      sourceTeams: [],
      targetTeams: [],
      rosterPlayers: [],
      organizations: [], // Clear organizations to trigger reload
      currentStep: 'source',
      showConfirmation: false,
      validationErrors: {},
      orgLoadError: null,
      teamLoadError: null,
      sourceTeamLoadError: null,
      targetTeamLoadError: null,
      playerLoadError: null,
      submitError: null,
      isLoadingOrgs: true // Trigger reload
    }))
    
    // Reload organizations after reset
    loadOrganizations()
  }, [reset, loadOrganizations])

  /**
   * Enhanced form submission with support for multiple equipment transfers
   * Based on equipmentTransferMode, either transfers player with all equipment or player only
   */
  const onSubmit = useCallback(async (data: MovePlayerHelmetFormData) => {
    // Manual validation since we removed register calls
    const validationErrors: Record<string, string> = {}
    
    if (!watchedSourceOrgID) validationErrors.sourceOrgID = 'Source organization is required'
    if (state.sourceTeams.length > 1 && !watchedSourceTeamID) validationErrors.sourceTeamID = 'Source team is required'
    if (!watchedPlayerID) validationErrors.playerID = 'Player selection is required'
    if (!watchedTargetOrgID) validationErrors.targetOrgID = 'Target organization is required'
    if (state.targetTeams.length > 1 && !watchedTargetTeamID) validationErrors.targetTeamID = 'Target team is required'
    if (!data.confirmTransfer) validationErrors.confirmTransfer = 'Please confirm the transfer'
    
    if (Object.keys(validationErrors).length > 0) {
      setState(prev => ({ ...prev, validationErrors }))
      addAlert('error', 'Please complete all required fields.')
      return
    }
    
    if (!state.selectedPlayer || !state.sourceTeam || !state.targetTeam) {
      addAlert('error', 'Please complete all selections before submitting.')
      return
    }

    // Validate transfer
    const validation = validatePlayerTransfer(
      state.selectedPlayer,
      state.sourceTeam,
      state.targetTeam,
      [] // TODO: Load target team roster for duplicate checking
    )

    if (!validation.valid) {
      addAlert('error', validation.errorMessages.join(', '))
      return
    }

    if (restrictedEquipment.length > 0 && (data.equipmentTransferMode || 'with-equipment') === 'with-equipment') {
      if (isValidatingRestrictedHelmets || restrictedHelmetChecks === null) {
        addAlert('error', restrictedHelmetError || 'Still validating Convertible / SF Echo helmet requirements. Please try again in a moment.')
        return
      }

      if (invalidRestrictedHelmets.length > 0) {
        const required = Array.from(new Set(invalidRestrictedHelmets.map(check => `"${check.requiredPlayerName}"`))).join(', ')
        addAlert('error', `The target team must have a matching generic player (${required}) to receive the Convertible / SF Echo helmet(s).`)
        return
      }
    }

    setState(prev => ({ 
      ...prev, 
      isSubmitting: true,
      submitError: null
    }))
    
    try {
      const equipmentMode = data.equipmentTransferMode || 'with-equipment'
      const playerName = formatPlayerName(state.selectedPlayer)
      const sourceDisplay = `${state.sourceOrg?.orgName || 'source org'} - ${state.sourceTeam?.teamName || 'source team'}`
      const targetDisplay = `${state.targetOrg?.orgName || 'target org'} - ${state.targetTeam?.teamName || 'target team'}`
      
      logger.info('Starting player transfer', {
        playerID: state.selectedPlayer.playerID,
        sourceTeam: state.sourceTeam.teamName,
        targetTeam: state.targetTeam.teamName,
        equipmentMode,
        equipmentCount: state.selectedPlayer.equipment?.length || 0
      })

      if (equipmentMode === 'without-equipment') {
        // Transfer player WITHOUT equipment (equipment stays with source team, unassigned from player)
        logger.info('Transferring player without equipment', {
          playerID: state.selectedPlayer.playerID
        })
        
        const payload = {
          teamID: state.targetTeam.teamID,
          firstName: state.selectedPlayer.firstName,
          middleInitial: state.selectedPlayer.middleInitial || null,
          lastName: state.selectedPlayer.lastName,
          dateOfBirth: state.selectedPlayer.dateOfBirth || null,
          positionID: state.selectedPlayer.positionID || 2,
          levelsClassID: state.selectedPlayer.levelsClassID || null,
          playerID: state.selectedPlayer.playerID,
          playerUnitID: null,  // No equipment transfer - null playerUnitID
          graduationYear: null,
          playerHeadCircumferenceInch: state.selectedPlayer.playerHeadCircumferenceInch || null,
        }

        await API.PutPlayersTeamsEquipment(payload)
        
        window.scrollTo({ top: 0, behavior: 'smooth' })
        addAlert('success', `Player ${playerName} successfully transferred from ${sourceDisplay} to ${targetDisplay}. Equipment remained with source team.`)
      } else {
        // Transfer player WITH all equipment
        const equipment = state.selectedPlayer.equipment || []
        
        if (equipment.length === 0) {
          // No equipment - transfer player only
          logger.info('Player has no equipment, transferring player only', {
            playerID: state.selectedPlayer.playerID
          })
          
          const payload = {
            teamID: state.targetTeam.teamID,
            firstName: state.selectedPlayer.firstName,
            middleInitial: state.selectedPlayer.middleInitial || null,
            lastName: state.selectedPlayer.lastName,
            dateOfBirth: state.selectedPlayer.dateOfBirth || null,
            positionID: state.selectedPlayer.positionID || 2,
            levelsClassID: state.selectedPlayer.levelsClassID || null,
            playerID: state.selectedPlayer.playerID,
            playerUnitID: null,
            graduationYear: null,
            playerHeadCircumferenceInch: state.selectedPlayer.playerHeadCircumferenceInch || null,
          }

          await API.PutPlayersTeamsEquipment(payload)
          window.scrollTo({ top: 0, behavior: 'smooth' })
          addAlert('success', `Player ${playerName} successfully transferred from ${sourceDisplay} to ${targetDisplay}.`)
        } else {
          // Multiple equipment - make sequential API calls for each equipment
          logger.info('Transferring player with equipment', {
            playerID: state.selectedPlayer.playerID,
            equipmentCount: equipment.length
          })
          
          const failedEquipment: string[] = []
          
          // Transfer each equipment sequentially
          for (let i = 0; i < equipment.length; i++) {
            const eq = equipment[i]
            try {
              logger.info(`Transferring equipment ${i + 1} of ${equipment.length}`, {
                playerUnitID: eq.playerUnitID,
                serial: eq.playerUnitSerialString
              })
              
              const payload = {
                teamID: state.targetTeam.teamID,
                firstName: state.selectedPlayer.firstName,
                middleInitial: state.selectedPlayer.middleInitial || null,
                lastName: state.selectedPlayer.lastName,
                dateOfBirth: state.selectedPlayer.dateOfBirth || null,
                positionID: state.selectedPlayer.positionID || 2,
                levelsClassID: state.selectedPlayer.levelsClassID || null,
                playerID: state.selectedPlayer.playerID,
                playerUnitID: eq.playerUnitID,  // Transfer this specific equipment
                graduationYear: null,
                playerHeadCircumferenceInch: state.selectedPlayer.playerHeadCircumferenceInch || null,
              }

              await API.PutPlayersTeamsEquipment(payload)
              logger.info(`Equipment ${i + 1} transferred successfully`, {
                playerUnitID: eq.playerUnitID
              })
            } catch (equipmentError: any) {
              logger.error(`Failed to transfer equipment ${i + 1}`, {
                playerUnitID: eq.playerUnitID,
                error: equipmentError.message
              })
              failedEquipment.push(eq.playerUnitSerialString || `Unit ${eq.playerUnitID}`)
            }
          }
          
          window.scrollTo({ top: 0, behavior: 'smooth' })
          
          if (failedEquipment.length === 0) {
            // All equipment transferred successfully
            addAlert('success', `Player ${playerName} and ${equipment.length} helmet(s) successfully transferred from ${sourceDisplay} to ${targetDisplay}.`)
          } else if (failedEquipment.length < equipment.length) {
            // Some equipment transferred, some failed
            const transferred = equipment.length - failedEquipment.length
            addAlert('warning', `Player ${playerName} transferred. Successfully transferred ${transferred} helmet(s), but ${failedEquipment.length} helmet(s) failed: ${failedEquipment.join(', ')}`)
          } else {
            // All equipment failed to transfer
            addAlert('error', `Player transferred but failed to transfer helmets: ${failedEquipment.join(', ')}`)
          }
        }
      }
      
      // Reset form on success (or partial success)
      reset()
      setState(prev => ({ 
        ...prev,
        sourceOrg: null,
        sourceTeam: null,
        selectedPlayer: null,
        targetOrg: null,
        targetTeam: null,
        organizations: [],
        sourceTeams: [],
        targetTeams: [],
        rosterPlayers: [],
        isSubmitting: false,
        submitError: null,
        currentStep: 'source',
        showConfirmation: false,
        validationErrors: {}
      }))
      
      // Reload organizations for next use
      loadOrganizations()
      
    } catch (error: any) {
      setState(prev => ({ 
        ...prev, 
        isSubmitting: false,
        submitError: 'Transfer failed'
      }))
      
      // Simple error handling matching other features
      handleApiError(error, 'Transfer failed')
      logger.error('Player transfer failed', { 
        module: 'MovePlayerHelmetTool', 
        data: { error: error.message, playerID: state.selectedPlayer?.playerID } 
      })
    }
  }, [
    watchedSourceOrgID,
    watchedSourceTeamID, 
    watchedPlayerID,
    watchedTargetOrgID,
    watchedTargetTeamID,
    state.sourceTeams,
    state.targetTeams,
    state.selectedPlayer,
    state.sourceTeam,
    state.targetTeam,
    state.sourceOrg,
    state.targetOrg,
    restrictedEquipment,
    restrictedHelmetChecks,
    restrictedHelmetError,
    invalidRestrictedHelmets,
    isValidatingRestrictedHelmets,
    addAlert,
    reset,
    loadOrganizations,
    handleApiError
  ])

  // Handle source organization change with automatic team selection
  useEffect(() => {
    if (watchedSourceOrgID && watchedSourceOrgID !== '') {
      const orgIdNum = parseInt(watchedSourceOrgID.toString())
      if (!isNaN(orgIdNum)) {
        const selectedOrg = state.organizations.find(org => org.orgID === orgIdNum)
        if (selectedOrg) {
          handleOrganizationSelection(selectedOrg)
            .then(result => {
              if (!result.requiresTeamSelection && result.team) {
                // Auto-select the single team - consolidate all state updates
                setState(prev => ({ 
                  ...prev, 
                  sourceOrg: selectedOrg,
                  sourceTeams: result.teams,
                  sourceTeam: result.team, 
                  sourceTeamLoadError: null,
                  currentStep: 'player'
                }))
                setValue('sourceTeamID', result.team.teamID.toString())
                // Load roster players for the auto-selected team
                loadRosterPlayers(result.team.teamID)
              } else {
                // Multiple teams - user needs to select - consolidate state updates
                setState(prev => ({ 
                  ...prev, 
                  sourceOrg: selectedOrg,
                  sourceTeams: result.teams,
                  sourceTeam: null, 
                  currentStep: 'source',
                  sourceTeamLoadError: null
                }))
              }
              
              // Clear dependent selections when org changes
              setValue('playerID', '')
              setValue('targetOrgID', '')
              setValue('targetTeamID', '')
              setValue('confirmTransfer', false)
            })
            .catch(error => {
            const msg = error instanceof Error ? error.message : String(error)
            const isNoTeams = typeof msg === 'string' && msg.toLowerCase().includes('no teams found for organization')
            if (isNoTeams) {
              setState(prev => ({ ...prev, sourceOrg: null, sourceTeams: [], sourceTeamLoadError: `No teams found for organization ${selectedOrg.orgName}` }))
              logger.info('No teams found for source organization', { module: 'MovePlayerHelmetTool', data: { orgID: selectedOrg.orgID } })
            } else {
              handleApiError(error, 'Failed to load teams for organization')
              setState(prev => ({ ...prev, sourceOrg: null, sourceTeams: [], sourceTeamLoadError: null }))
            }
            })
        }
      }
    } else {
      setState(prev => ({ 
        ...prev, 
        sourceOrg: null,
        sourceTeam: null,
        selectedPlayer: null,
        targetOrg: null,
        targetTeam: null,
        sourceTeams: [],
        targetTeams: [],
        rosterPlayers: [],
        currentStep: 'source',
        sourceTeamLoadError: null,
        targetTeamLoadError: null
      }))
      setValue('sourceTeamID', '')
      setValue('playerID', '')
      setValue('targetOrgID', '')
      setValue('targetTeamID', '')
      setValue('confirmTransfer', false)
    }
  }, [watchedSourceOrgID, state.organizations, setValue, handleApiError, loadRosterPlayers])

  // Computed values for inline rendering
  const availableTargetOrgs = useMemo(() => 
    state.organizations.filter(org => 
      org.orgName && org.orgName.trim() !== '' &&
      (state.sourceOrg ? org.orgID !== state.sourceOrg.orgID : true)
    ), [state.organizations, state.sourceOrg]
  )

  const playerOptions = useMemo(() => 
    Array.isArray(state.rosterPlayers) && state.rosterPlayers.length > 0
      ? state.rosterPlayers.filter(p => p && p.playerID != null).map(player => ({
          value: player.playerID ? player.playerID.toString() : '',
          label: player ? formatPlayerName(player) : 'Unknown Player',
          player
        }))
      : [],
    [state.rosterPlayers]
  )

  return (
    <div className="w-full max-w-7xl mx-auto px-2 py-2 sm:px-4 sm:py-4 lg:px-6 lg:py-6 bg-neutral-50 min-h-screen">
      {/* Riddell Header - Match MoveHelmetOrgFlow styling */}
      <div className="bg-riddell-red text-white rounded-t-lg shadow-lg mb-3 sm:mb-4 lg:mb-6 w-full">
        <div className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 lg:py-6">
          <div className="flex flex-col sm:flex-row items-center sm:space-x-4 space-y-2 sm:space-y-0 w-full">
            <div className="bg-white p-1.5 sm:p-2 rounded flex-shrink-0">
              <div className="w-6 h-6 sm:w-8 sm:h-8 bg-riddell-red rounded flex items-center justify-center">
                <span className="text-white font-bold text-sm sm:text-lg">R</span>
              </div>
            </div>
            <div className="text-center sm:text-left flex-1 min-w-0">
              <h1 
                className="text-lg sm:text-2xl lg:text-3xl font-heading font-black tracking-tight"
                id="page-title"
              >
                Move Player & Helmet
              </h1>
              <p className="text-riddell-red-100 text-sm sm:text-base lg:text-lg font-medium" id="page-description">
                Transfer players and equipment between organizations
              </p>
            </div>
            {/* Reset Button */}
            <ResetButton
              isVisible={!!(state.sourceOrg || state.selectedPlayer || state.targetOrg || state.orgLoadError || state.sourceTeamLoadError || state.targetTeamLoadError || state.playerLoadError)}
              onReset={handleReset}
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-xl border border-neutral-200 w-full overflow-visible">
        <div className="p-3 sm:p-4 lg:p-6 w-full">

          <form 
            onSubmit={handleSubmit(onSubmit)} 
            className="space-y-4 sm:space-y-6 lg:space-y-8 w-full"
            aria-labelledby="page-title"
            aria-describedby="page-description"
            noValidate
          >
            {/* Step 1: Source Organization & Team Selection */}
            <div className="bg-neutral-50 border-l-4 border-riddell-red p-3 sm:p-4 lg:p-6 rounded-lg w-full" role="group" aria-labelledby="source-selection-heading">
              <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3 mb-3 sm:mb-4">
                <div className="bg-riddell-red text-white rounded-full w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center font-bold text-xs sm:text-sm lg:text-base flex-shrink-0">1</div>
                <h2 id="source-selection-heading" className="text-base sm:text-lg lg:text-xl font-heading font-bold text-riddell-black">SELECT SOURCE ORGANIZATION</h2>
              </div>

              <div className="w-full">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                  {/* Source Organization */}
                  <div>
                    <label htmlFor="sourceOrgID" className="block text-xs sm:text-sm font-bold text-riddell-black mb-2 uppercase tracking-wide">
                      Source Organization
                    </label>
                    <SearchableDropdown
                      {...register('sourceOrgID', { required: 'Source organization is required' })}
                      id="sourceOrgID"
                      name="sourceOrgID"
                      value={watchedSourceOrgID || ''}
                      onChange={(value) => {
                        setValue('sourceOrgID', value, { shouldValidate: true, shouldTouch: true, shouldDirty: true })
                      }}
                      options={state.organizations
                        .filter(org => org.orgName && org.orgName.trim() !== '')
                        .map(org => ({ value: org.orgID.toString(), label: org.orgName }))}
                      placeholder="Select source organization..."
                      disabled={state.isLoadingOrgs}
                      error={errors.sourceOrgID?.message}
                      required={true}
                      aria-busy={state.isLoadingOrgs}
                    />
                            {state.orgLoadError && (
                      <p className="mt-2 text-sm text-error font-medium flex items-center" role="alert">
                        <span className="mr-1">⚠</span>
                        {state.orgLoadError}
                      </p>
                    )}
                            {state.sourceTeamLoadError && (
                              <p className="mt-2 text-sm text-error font-medium flex items-center" role="alert">
                                <span className="mr-1">⚠</span>
                                {state.sourceTeamLoadError}
                              </p>
                            )}
                    {state.isLoadingOrgs && (
                      <div className="mt-3 flex items-center space-x-2 sm:space-x-3 text-riddell-red">
                        <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-2 border-riddell-red border-t-transparent"></div>
                        <span className="text-sm font-medium">Loading organizations...</span>
                      </div>
                    )}
                  </div>

                  {/* Source Team Dropdown - only show if multiple teams */}
                  {state.sourceOrg && state.sourceTeams.length > 1 && (
                    <div>
                      <label htmlFor="sourceTeamID" className="block text-xs sm:text-sm font-bold text-riddell-black mb-2 uppercase tracking-wide">
                        Source Team
                      </label>
                      <SearchableDropdown
                        {...register('sourceTeamID', { required: 'Source team is required' })}
                        id="sourceTeamID"
                        name="sourceTeamID"
                        value={watchedSourceTeamID || ''}
                        onChange={(value) => {
                          setValue('sourceTeamID', value, { shouldValidate: true, shouldTouch: true, shouldDirty: true })
                        }}
                        options={state.sourceTeams.map(team => ({ value: team.teamID.toString(), label: team.teamName }))}
                        placeholder="Select source team..."
                        disabled={state.isLoadingTeams}
                        error={errors.sourceTeamID?.message}
                        required={true}
                      />
                      {state.isLoadingTeams && (
                        <div className="mt-3 flex items-center space-x-2 sm:space-x-3 text-riddell-red">
                          <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-2 border-riddell-red border-t-transparent"></div>
                          <span className="text-sm font-medium">Loading teams...</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Source Team (Auto-selected) - only show if exactly one team */}
                  {state.sourceOrg && state.sourceTeams.length === 1 && (
                    <div>
                      <label className="block text-xs sm:text-sm font-bold text-riddell-black mb-2 uppercase tracking-wide">
                        Source Team (Auto-selected)
                      </label>
                      <div className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-base sm:text-lg font-medium bg-gray-100 border-2 border-gray-300 rounded-md">
                        {state.sourceTeams[0]?.teamName || 'Loading...'}
                      </div>
                    </div>
                  )}
                </div>

                {/* Team Information Display */}
                {state.sourceOrg && state.sourceTeam && (
                  <div className="mt-3 p-3 bg-success/10 border border-success/20 rounded-md">
                    <div className="flex items-center space-x-2 text-success">
                      <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-sm font-medium">
                        Selected: {createOrgTeamDisplayName(state.sourceOrg, state.sourceTeam)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Step 2: Player Selection */}
            {state.sourceOrg && state.sourceTeam && (
              <div className="bg-neutral-50 border-l-4 border-riddell-red p-3 sm:p-4 lg:p-6 rounded-lg w-full" role="group" aria-labelledby="player-selection-heading">
                <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3 mb-3 sm:mb-4">
                  <div className="bg-riddell-red text-white rounded-full w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center font-bold text-xs sm:text-sm lg:text-base flex-shrink-0">2</div>
                  <h2 id="player-selection-heading" className="text-base sm:text-lg lg:text-xl font-heading font-bold text-riddell-black">SELECT PLAYER TO MOVE</h2>
                </div>

                {state.sourceOrg && state.sourceTeam && (
                  <div className="mb-4 p-3 bg-riddell-red-50 border border-riddell-red-200 rounded-lg">
                    <h3 className="text-sm font-bold text-riddell-black mb-1">Selected Source</h3>
                    <p className="text-sm text-gray-700">{state.sourceOrg.orgName} - {state.sourceTeam.teamName}</p>
                  </div>
                )}

                <div>
                  <label htmlFor="playerID" className="block text-xs sm:text-sm font-bold text-riddell-black mb-2 uppercase tracking-wide">
                    Player
                  </label>
                  <SearchableDropdown
                    {...register('playerID', { required: 'Player selection is required' })}
                    id="playerID"
                    name="playerID"
                    value={watchedPlayerID || ''}
                    onChange={(value) => {
                      setValue('playerID', value, { shouldValidate: true, shouldTouch: true, shouldDirty: true })
                    }}
                    options={playerOptions.map(option => ({ value: option.value, label: option.label }))}
                    placeholder="Select player..."
                    disabled={state.isLoadingPlayers || state.rosterPlayers.length === 0}
                    error={errors.playerID?.message}
                    required={true}
                    aria-busy={state.isLoadingPlayers}
                  />
                  {state.playerLoadError && (
                    <p className="mt-2 text-sm text-error font-medium flex items-center" role="alert">
                      <span className="mr-1">⚠</span>
                      {state.playerLoadError}
                    </p>
                  )}
                  {state.isLoadingPlayers && (
                    <div className="mt-3 flex items-center space-x-2 sm:space-x-3 text-riddell-red">
                      <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-2 border-riddell-red border-t-transparent"></div>
                      <span className="text-sm font-medium">Loading players...</span>
                    </div>
                  )}
                  {state.rosterPlayers.length === 0 && !state.isLoadingPlayers && watchedSourceTeamID && (
                    <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg" role="alert">
                      <p className="text-sm text-yellow-800">No players available to move from this team.</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 3: Player Information Display */}
            {state.selectedPlayer && (
              <div className="bg-success/10 border-l-4 border-success p-3 sm:p-4 lg:p-6 rounded-lg w-full" role="group" aria-labelledby="player-info-heading">
                <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3 mb-3 sm:mb-4">
                  <div className="bg-success text-white rounded-full w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center font-bold text-xs sm:text-sm lg:text-base flex-shrink-0">3</div>
                  <h3 id="player-info-heading" className="text-base sm:text-lg lg:text-xl font-heading font-bold text-riddell-black">PLAYER INFORMATION</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-2 sm:gap-3 text-xs sm:text-sm w-full">
                  <div className="bg-white p-2 sm:p-3 rounded-md shadow-sm w-full">
                    <span className="block text-xs font-bold text-riddell-black uppercase tracking-wide mb-0.5">Player Name</span>
                    <span className="text-sm sm:text-base font-medium text-riddell-black break-words leading-tight">{formatPlayerName(state.selectedPlayer)}</span>
                  </div>
                </div>

                {/* Equipment List Display */}
                {state.selectedPlayer.equipment && state.selectedPlayer.equipment.length > 0 && (
                  <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-success/20">
                    <h4 className="text-sm sm:text-base font-bold text-riddell-black mb-3 sm:mb-4 uppercase tracking-wide">
                      Assigned Equipment ({state.selectedPlayer.equipment.length})
                    </h4>
                    <div className="space-y-2 sm:space-y-3">
                      {state.selectedPlayer.equipment.map((eq, idx) => (
                        <div key={idx} className="bg-white p-3 sm:p-4 rounded-md border border-success/20 shadow-sm">
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
                            {eq.playerUnitSerialString && (
                              <div>
                                <span className="block text-xs font-bold text-riddell-black uppercase tracking-wide mb-0.5">Serial #</span>
                                <span className="text-sm font-medium text-riddell-black">{eq.playerUnitSerialString}</span>
                              </div>
                            )}
                            {eq.helmetRFIDLabel && (
                              <div>
                                <span className="block text-xs font-bold text-riddell-black uppercase tracking-wide mb-0.5">RFID Label</span>
                                <span className="text-sm font-bold text-riddell-red">{eq.helmetRFIDLabel}</span>
                              </div>
                            )}
                            {eq.helmetType && (
                              <div>
                                <span className="block text-xs font-bold text-riddell-black uppercase tracking-wide mb-0.5">Type</span>
                                <span className="text-sm font-medium text-riddell-black">{eq.helmetType}</span>
                              </div>
                            )}
                            {eq.features && (
                              <div>
                                <span className="block text-xs font-bold text-riddell-black uppercase tracking-wide mb-0.5">Features</span>
                                <span className="text-sm font-medium text-riddell-black">{formatFeatures(eq.features)}</span>
                              </div>
                            )}
                            {eq.helmetHatSize && (
                              <div>
                                <span className="block text-xs font-bold text-riddell-black uppercase tracking-wide mb-0.5">Hat Size</span>
                                <span className="text-sm font-medium text-riddell-black">{eq.helmetHatSize}</span>
                              </div>
                            )}
                            {eq.rfidBarcode && (
                              <div>
                                <span className="block text-xs font-bold text-riddell-black uppercase tracking-wide mb-0.5">Barcode</span>
                                <span className="text-sm font-medium text-riddell-black break-all">{eq.rfidBarcode}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Step 4: Target Organization & Team Selection */}
            {state.selectedPlayer && (
              <div className="bg-neutral-50 border-l-4 border-riddell-red p-3 sm:p-4 lg:p-6 rounded-lg w-full">
                <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3 mb-3 sm:mb-4">
                  <div className="bg-riddell-red text-white rounded-full w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center font-bold text-xs sm:text-sm lg:text-base flex-shrink-0">4</div>
                  <h2 className="text-base sm:text-lg lg:text-xl font-heading font-bold text-riddell-black">SELECT TARGET ORGANIZATION & TEAM</h2>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                  {/* Target Organization */}
                  <div>
                    <label htmlFor="targetOrgID" className="block text-xs sm:text-sm font-bold text-riddell-black mb-2 uppercase tracking-wide">
                      Target Organization
                    </label>
                    <SearchableDropdown
                      {...register('targetOrgID', { required: 'Target organization is required' })}
                      id="targetOrgID"
                      name="targetOrgID"
                      value={watchedTargetOrgID || ''}
                      onChange={(value) => {
                        setValue('targetOrgID', value, { shouldValidate: true, shouldTouch: true, shouldDirty: true })
                      }}
                      options={availableTargetOrgs.map(org => ({ value: org.orgID.toString(), label: org.orgName }))}
                      placeholder="Select target organization..."
                      error={errors.targetOrgID?.message}
                      required={true}
                    />
                    {state.targetTeamLoadError && (
                      <>
                        <p className="mt-2 text-sm text-error font-medium flex items-center" role="alert">
                          <span className="mr-1">⚠</span>
                          {state.targetTeamLoadError}
                        </p>
                        <p className="mt-1 text-xs text-error">Please select a new target organization</p>
                      </>
                    )}
                  </div>

                  {/* Target Team */}
                  {watchedTargetOrgID && state.targetTeams.length > 1 && (
                    <div>
                      <label htmlFor="targetTeamID" className="block text-xs sm:text-sm font-bold text-riddell-black mb-2 uppercase tracking-wide">
                        Target Team
                      </label>
                      <SearchableDropdown
                        {...register('targetTeamID', { required: 'Target team is required when multiple teams exist' })}
                        id="targetTeamID"
                        name="targetTeamID"
                        value={watchedTargetTeamID || ''}
                        onChange={(value) => {
                          setValue('targetTeamID', value, { shouldValidate: true, shouldTouch: true, shouldDirty: true })
                        }}
                        options={state.targetTeams.map(team => ({ value: team.teamID.toString(), label: team.teamName }))}
                        placeholder="Select target team..."
                        disabled={state.isLoadingTeams}
                        error={errors.targetTeamID?.message}
                        required={true}
                      />
                      {state.isLoadingTeams && (
                        <div className="mt-3 flex items-center space-x-2 sm:space-x-3 text-riddell-red">
                          <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-2 border-riddell-red border-t-transparent"></div>
                          <span className="text-sm font-medium">Loading teams...</span>
                        </div>
                      )}
                    </div>
                  )}

                  {watchedTargetOrgID && state.targetTeams.length === 1 && (
                    <div>
                      <label className="block text-xs sm:text-sm font-bold text-riddell-black mb-2 uppercase tracking-wide">
                        Target Team (Auto-selected)
                      </label>
                      <div className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-base sm:text-lg font-medium bg-gray-100 border-2 border-gray-300 rounded-md">
                        {state.targetTeams[0]?.teamName || 'Loading...'}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 5: Confirmation & Transfer */}
            {state.selectedPlayer && state.targetOrg && (state.targetTeams.length === 1 || (state.targetTeams.length > 1 && state.targetTeam)) && (
              <div className="bg-neutral-50 border-l-4 border-riddell-red p-3 sm:p-4 lg:p-6 rounded-lg w-full">
                <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3 mb-3 sm:mb-4">
                  <div className="bg-riddell-red text-white rounded-full w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center font-bold text-xs sm:text-sm lg:text-base flex-shrink-0">5</div>
                  <h2 className="text-base sm:text-lg lg:text-xl font-heading font-bold text-riddell-black">CONFIRM TRANSFER</h2>
                </div>

                {state.selectedPlayer && state.sourceOrg && state.sourceTeam && state.targetOrg && (
                  <div className="mb-6 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                        <h3 className="text-sm font-bold text-red-900 mb-2">FROM</h3>
                            <p className="text-sm font-medium text-red-800"><span className="font-bold">Organization: </span>{state.sourceOrg.orgName}</p>
                            <p className="text-sm font-medium text-red-800"><span className="font-bold">Team: </span>{state.sourceTeam.teamName}</p>
                            <p className="text-sm font-medium text-red-800 mt-2"><span className="font-bold">Player: </span>{formatPlayerName(state.selectedPlayer)}</p>
                            {watchedEquipmentMode === 'with-equipment' && state.selectedPlayer.equipment && state.selectedPlayer.equipment.length > 0 && (
                              <p className="text-sm font-medium text-red-800"><span className="font-bold">Equipment: </span>{state.selectedPlayer.equipment.length} helmet(s)</p>
                            )}
                      </div>
                      <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                        <h3 className="text-sm font-bold text-green-900 mb-2">TO</h3>
                        <p className="text-sm font-medium text-green-800"><span className="font-bold">Organization: </span>{state.targetOrg.orgName}</p>
                        <p className="text-sm font-medium text-green-800"><span className="font-bold">Team: </span>{state.targetTeam?.teamName || state.targetTeams[0]?.teamName}</p>
                        <p className="text-sm font-medium text-green-800 mt-2"><span className="font-bold">Player: </span>{formatPlayerName(state.selectedPlayer)}</p>
                        {watchedEquipmentMode === 'with-equipment' && state.selectedPlayer.equipment && state.selectedPlayer.equipment.length > 0 && (
                          <p className="text-sm font-medium text-green-800"><span className="font-bold">Equipment: </span>{state.selectedPlayer.equipment.length} helmet(s)</p>
                        )}
                      </div>
                    </div>

                    {/* Equipment Transfer Mode Selection */}
                    {state.selectedPlayer.equipment && state.selectedPlayer.equipment.length > 0 ? (
                      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <h3 className="text-sm font-bold text-blue-900 mb-3">TRANSFER MODE</h3>
                        <div className="space-y-2">
                          <label className="flex items-center space-x-3 cursor-pointer">
                            <input
                              {...register('equipmentTransferMode')}
                              type="radio"
                              value="with-equipment"
                              className="h-4 w-4 text-riddell-red border-gray-300 focus:ring-riddell-red"
                            />
                            <span className="text-sm text-blue-800">
                              <span className="font-bold">Transfer with equipment</span>
                              <span className="text-blue-700"> - Move player and all {state.selectedPlayer.equipment.length} helmet(s)</span>
                            </span>
                          </label>
                          <label className="flex items-center space-x-3 cursor-pointer">
                            <input
                              {...register('equipmentTransferMode')}
                              type="radio"
                              value="without-equipment"
                              className="h-4 w-4 text-riddell-red border-gray-300 focus:ring-riddell-red"
                            />
                            <span className="text-sm text-blue-800">
                              <span className="font-bold">Transfer without equipment</span>
                              <span className="text-blue-700"> - Move player only, leave {state.selectedPlayer.equipment.length} helmet(s) with source team</span>
                            </span>
                          </label>
                        </div>
                      </div>
                    ) : (
                      <div className="p-4 bg-neutral-50 border border-neutral-200 rounded-lg">
                        <p className="text-sm text-neutral-700"><span className="font-bold">Transfer Mode:</span> Player has no equipment assigned. Equipment will not be transferred.</p>
                      </div>
                    )}

                    {/* Convertible / SF Echo target roster requirement */}
                    {restrictedEquipment.length > 0 && (
                      <div className="p-4 bg-warning/10 border border-warning/30 rounded-lg" role="alert">
                        <h3 className="text-sm font-bold text-warning uppercase tracking-wide mb-2">Convertible / SF Echo Helmet Requirement</h3>
                        {isValidatingRestrictedHelmets ? (
                          <div className="flex items-center space-x-2 text-riddell-red">
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-riddell-red border-t-transparent"></div>
                            <span className="text-sm font-medium text-riddell-black">Validating helmet requirements against the target team roster...</span>
                          </div>
                        ) : restrictedHelmetError ? (
                          <p className="text-sm text-error font-medium">{restrictedHelmetError}</p>
                        ) : (
                          <ul className="space-y-2">
                            {(restrictedHelmetChecks || []).map(check => (
                              <li key={check.playerUnitID} className="text-sm text-riddell-black">
                                <span className="font-bold">{check.label}</span> ({check.features}) —{' '}
                                {check.isValid ? (
                                  <span className="text-success font-medium">Target team has a matching "{check.requiredPlayerName}" player.</span>
                                ) : (
                                  <span className="text-error font-medium">
                                    Target team must have a player named "{check.requiredPlayerName}" to receive this helmet.
                                  </span>
                                )}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}

                    <div className="flex items-start space-x-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <input
                        {...register('confirmTransfer', { required: 'You must confirm the transfer to proceed' })}
                        type="checkbox"
                        id="confirmTransfer"
                        className="mt-0.5 h-4 w-4 text-riddell-red border-gray-300 rounded focus:ring-riddell-red"
                      />
                      <label htmlFor="confirmTransfer" className="text-sm text-yellow-800">
                        <span className="font-bold">Confirm Transfer:</span> 
                        {watchedEquipmentMode === 'without-equipment' ? (
                          <span> I understand that this action will move the player to the target organization and team without their assigned equipment. The equipment will remain unassigned with the source team.</span>
                        ) : (
                          <span> I understand that this action will move the player and their assigned equipment to the target organization and team.</span>
                        )}
                      </label>
                    </div>
                    {errors.confirmTransfer && (
                      <p className="mt-2 text-sm text-error font-medium flex items-center" role="alert">
                        <span className="mr-1">⚠</span>
                        {errors.confirmTransfer.message}
                      </p>
                    )}
                    {state.validationErrors.confirmTransfer && (
                      <p className="mt-2 text-sm text-error font-medium flex items-center" role="alert">
                        <span className="mr-1">⚠</span>
                        {state.validationErrors.confirmTransfer}
                      </p>
                    )}
                  </div>
                )}

                <div className="flex flex-col sm:flex-row sm:justify-end space-y-3 sm:space-y-0 sm:space-x-3">
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={state.isSubmitting || !watch('confirmTransfer') || isRestrictedTransferBlocked}
                    className="w-full py-3 sm:py-4 text-base sm:text-lg font-bold uppercase tracking-wide bg-riddell-red text-white border-2 border-riddell-red rounded-md hover:bg-riddell-red-dark disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150"
                  >
                    {state.isSubmitting ? (
                      <div className="flex items-center justify-center space-x-2 sm:space-x-3">
                        <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-2 border-white border-t-transparent"></div>
                        <span>TRANSFERRING...</span>
                      </div>
                    ) : watchedEquipmentMode === 'without-equipment' ? (
                      'TRANSFER PLAYER ONLY'
                    ) : (
                      'TRANSFER PLAYER & HELMET'
                    )}
                  </Button>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  )
}

export default MovePlayerHelmetTool
